import React, { useState, useEffect } from 'react';
import { 
  Modal, Form, Select, Radio, Button, Input,
  Spin, notification, Alert, Typography, Divider
} from 'antd';
import { 
  InfoCircleOutlined, CheckCircleOutlined, 
  CloseCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import ordersService from '../../../../services/ordersService';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

/**
 * Order Status Update Modal Component
 */
const StatusModal = ({ visible, orderId, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Fetch order data when modal opens
  useEffect(() => {
    if (visible && orderId) {
      fetchOrderData();
    }
  }, [visible, orderId]);
  
  const fetchOrderData = async () => {
    try {
      setLoading(true);
      const response = await ordersService.getOrderById(orderId);
      setOrder(response.order || response);
      
      // Set default form value to current status
      form.setFieldsValue({
        order_status: response.order ? response.order.order_status : response.order_status
      });
    } catch (error) {
      notification.error({
        message: 'Error loading order',
        description: error.message || 'Failed to load order data'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      
      const { order_status, notes } = values;
      
      await ordersService.updateOrderStatus(orderId, order_status, notes);
      
      notification.success({
        message: 'Order status updated',
        description: `Order #${orderId} status has been updated to ${order_status}`
      });
      
      onSuccess();
    } catch (error) {
      notification.error({
        message: 'Failed to update order status',
        description: error.message || 'Please check your inputs and try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Helper function to get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Scheduled':
        return <ClockCircleOutlined className="text-blue-500" />;
      case 'En-route':
        return <ClockCircleOutlined className="text-orange-500" />;
      case 'Completed':
        return <CheckCircleOutlined className="text-green-500" />;
      case 'Collected':
        return <CheckCircleOutlined className="text-purple-500" />;
      case 'Cancelled':
        return <CloseCircleOutlined className="text-red-500" />;
      case 'Failed':
        return <WarningOutlined className="text-red-500" />;
      default:
        return <InfoCircleOutlined />;
    }
  };

 
  // Helper function to get description for status
  const getStatusDescription = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'The order is scheduled for delivery.';
      case 'En-route':
        return 'Delivery is in progress.';
      case 'Completed':
        return 'The order has been successfully delivered.';
      case 'Collected':
        return 'The items have been collected from the customer.';
      case 'Cancelled':
        return 'The order has been cancelled.';
      case 'Failed':
        return 'The order could not be completed.';
      default:
        return '';
    }
  };
  
  return (
    <Modal
      title={`Update Order Status #${orderId}`}
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={submitting} 
          onClick={handleSubmit}
          className="bg-golden-navy"
        >
          Update Status
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        {order ? (
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              order_status: order.order_status
            }}
          >
            <Alert
              message="Current Status"
              description={
                <div className="flex items-center mt-2">
                  {getStatusIcon(order.order_status)}
                  <Text strong className="ml-2">{order.order_status}</Text>
                  <Text className="ml-4 text-gray-500">
                    {getStatusDescription(order.order_status)}
                  </Text>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />
            
            <Form.Item
              name="order_status"
              label="New Status"
              rules={[{ required: true, message: 'Please select a status' }]}
            >
              <Radio.Group buttonStyle="solid" className="w-full">
                <div className="grid grid-cols-2 gap-2">
                  <Radio.Button value="Scheduled" className="text-center">
                    <div className="py-1">
                      <ClockCircleOutlined className="mr-1 text-blue-500" />
                      Scheduled
                    </div>
                  </Radio.Button>
                  <Radio.Button value="En-route" className="text-center">
                    <div className="py-1">
                      <ClockCircleOutlined className="mr-1 text-orange-500" />
                      En-route
                    </div>
                  </Radio.Button>
                  <Radio.Button value="Completed" className="text-center">
                    <div className="py-1">
                      <CheckCircleOutlined className="mr-1 text-green-500" />
                      Completed
                    </div>
                  </Radio.Button>
                  <Radio.Button value="Collected" className="text-center">
                    <div className="py-1">
                      <CheckCircleOutlined className="mr-1 text-purple-500" />
                      Collected
                    </div>
                  </Radio.Button>
                  <Radio.Button value="Cancelled" className="text-center">
                    <div className="py-1">
                      <CloseCircleOutlined className="mr-1 text-red-500" />
                      Cancelled
                    </div>
                  </Radio.Button>
                  <Radio.Button value="Failed" className="text-center">
                    <div className="py-1">
                      <WarningOutlined className="mr-1 text-red-500" />
                      Failed
                    </div>
                  </Radio.Button>
                </div>
              </Radio.Group>
            </Form.Item>
            
            <Form.Item
              name="notes"
              label="Status Update Notes"
              extra="Add any additional notes about this status change (optional)"
            >
              <TextArea 
                rows={4} 
                placeholder="Enter notes about this status change" 
              />
            </Form.Item>
            
            <Divider />
            
            <Alert
              message="Status Change Implications"
              description={
                <ul className="list-disc pl-6 mt-2">
                  <li>Changing to <strong>Scheduled</strong> indicates the order is scheduled for delivery.</li>
                  <li>Changing to <strong>En-route</strong> indicates the delivery is in progress.</li>
                  <li>Changing to <strong>Completed</strong> indicates the order has been successfully delivered.</li>
                  <li>Changing to <strong>Collected</strong> indicates the items have been collected from the customer.</li>
                  <li>Changing to <strong>Cancelled</strong> or <strong>Failed</strong> may affect reports and analytics.</li>
                </ul>
              }
              type="warning"
              showIcon
              className="mt-4"
            />
          </Form>
        ) : (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
            <p className="ml-3 text-gray-500">Loading order data...</p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default StatusModal;